import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { getActionsFromDb } from "../helpers/actions/getActionsFromDb"
import {
  Action,
  updateActionOnDb,
  upsertActions,
} from "../helpers/database/actions/saveActions"
import { ActionInstruction } from "../helpers/openAi/actionManager"
import { useSession } from "next-auth/react"

interface GlobalContextProps {
  actions: (Action | ActionInstruction)[]
  setActions: Dispatch<SetStateAction<(Action | ActionInstruction)[]>>
  refreshActionDataInContext: () => Promise<void>
  loadingActions: boolean
  addActions: (newActions: (Action | ActionInstruction)[]) => Promise<void>
  updateAction: (updatedAction: Partial<Action>) => Promise<void>
  selectedActionId: string
  setSelectedActionId: Dispatch<React.SetStateAction<string>>
}

const GlobalContext = createContext<GlobalContextProps | undefined>(undefined)

interface GlobalProviderProps {
  children: ReactNode
}

export const ActionContext: React.FC<GlobalProviderProps> = ({ children }) => {
  const [actions, setActions] = useState<(Action | ActionInstruction)[]>([])
  const [selectedActionId, setSelectedActionId] = useState<string>("")
  const [loadingActions, setLoadingActions] = useState(false)
  const { data: session, status } = useSession()

  const refreshActionDataInContext = useCallback(async () => {
    if (!session?.user) {
      console.log("ActionContext: No session, skipping actions fetch")
      return
    }

    setLoadingActions(true)

    try {
      const actionData = await getActionsFromDb()
      if (actionData) {
        const { actions } = JSON.parse(actionData)
        await setActions(actions)
      }
    } catch (error) {
      console.error("Failed to fetch actions:", error)
    } finally {
      setLoadingActions(false)
    }
  }, [session?.user])

  useEffect(() => {
    // Only run when session is available
    if (status === "loading") return
    if (status === "unauthenticated") return

    if (session?.user) {
      // Defer action fetch until after initial render to improve page load performance
      // Use requestIdleCallback if available, otherwise fallback to setTimeout
      const deferFetch = () => {
        if (typeof window !== "undefined" && "requestIdleCallback" in window) {
          requestIdleCallback(
            () => {
              refreshActionDataInContext()
            },
            { timeout: 2000 }
          ) // Fallback after 2 seconds if browser is busy
        } else {
          // Fallback for browsers without requestIdleCallback
          setTimeout(() => {
            refreshActionDataInContext()
          }, 100) // Small delay to allow initial render
        }
      }
      deferFetch()
    }
  }, [session, status, refreshActionDataInContext])

  const addActions = useCallback(
    async (newActions: (Action | ActionInstruction)[]) => {
      // Optimistically add actions with a loading flag
      setActions((prev) => [
        ...prev,
        ...newActions.map((action) => ({ ...action, loading: true })),
      ])

      try {
        // Persist the changes to the database
        await upsertActions(newActions)

        // On success, remove the loading flag
        setActions((prev) =>
          prev.map((action) =>
            newActions.some(
              (newAction) => newAction.actionId === action.actionId
            )
              ? { ...action, loading: false }
              : action
          )
        )
      } catch (error) {
        // On failure, remove the optimistically added actions
        console.error("Failed to add actions:", error)

        setActions((prev) =>
          prev.filter(
            (action) =>
              !newActions.some(
                (newAction) => newAction.actionId === action.actionId
              )
          )
        )
      }
    },
    []
  )

  const updateAction = useCallback(async (updatedAction: Partial<Action>) => {
    // Optimistically update state
    setActions((prev) =>
      prev.map((action) =>
        action.actionId === updatedAction.actionId
          ? { ...action, ...updatedAction, loading: true }
          : action
      )
    )

    try {
      // Persist the changes to the database
      await updateActionOnDb(updatedAction)

      // Set the action's loading state to false on success
      setActions((prev) =>
        prev.map((action) =>
          action.actionId === updatedAction.actionId
            ? { ...action, loading: false }
            : action
        )
      )
    } catch (error) {
      // Revert the state if the update fails
      console.error("Failed to update action:", error)

      setActions((prev) =>
        prev.filter((action) => action.actionId !== updatedAction.actionId)
      )
    }
  }, [])

  return (
    <GlobalContext.Provider
      value={{
        actions,
        setActions,
        refreshActionDataInContext,
        loadingActions,
        addActions,
        updateAction,
        selectedActionId,
        setSelectedActionId,
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}

// Custom hook for accessing the global context
export const useActionContext = () => useContext(GlobalContext)
